import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Grid,
  styled,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid, GridSortDirection, GridToolbar } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import { useQuery } from "@apollo/client";
import { useDebounce } from "use-debounce";
import {
  GET_ORGANIZATIONS,
  GET_ORGANIZATIONS_COUNT,
} from "../../../graphql/queries";
import OrganizationsTable from "../../../Components/Tables/OrganizationsTable/OrganizationsTable";
import PageContainer from "../../Shared/PageContainer/PageContainer";
import { Organization } from "../../../../Types";
import {
  getFormattedMasjidStatus,
  getStatus,
} from "../../../Components/PageComponents/MasjidTable/Helper";
import toast from "react-hot-toast";
import moment from "moment";
import { FcPlus } from "react-icons/fc";
import OrganizationForm from "../../../Components/PageComponents/OrganizationForm/OrganizationForm";
import { useAppSelector } from "../../../Redux/hooks";
import { AdminInterFace } from "../../../Components/FeedComponents/Feed/Feed";
import FilterBuilder from "../../../Components/Shared/FilterBuilder/FilterBuilder";

const Organizations = () => {
  const [searchText, setSearchText] = useState("");
  const [debouncedSearchText] = useDebounce(searchText, 500); // 500ms delay
  const [selectedStatus, setSelectedStatus] = useState<
    "all" | "approved" | "pending" | "frozen"
  >("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [rowCount, setRowCount] = useState(0);
  const [sortModel, setSortModel] = useState<
    { field: string; sort: GridSortDirection }[]
  >([{ field: "updatedAt", sort: "desc" }]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [filterBuilderOpen, setFilterBuilderOpen] = useState(false);
  const [advancedFilter, setAdvancedFilter] = useState<string | null>(null);

  // Get admin info for permissions
  const adminString = localStorage.getItem("admin");
  const admin: AdminInterFace | null = adminString
    ? JSON.parse(adminString)
    : null;

  // Build search filter
  const buildSearchFilter = () => {
    if (!debouncedSearchText.trim()) return null;

    return JSON.stringify({
      $or: [
        { name: { $regex: debouncedSearchText, $options: "i" } },
        { address: { $regex: debouncedSearchText, $options: "i" } },
        { "assignedUser.name": { $regex: debouncedSearchText, $options: "i" } },
      ],
    });
  };

  // Build status filter
  const buildStatusFilter = () => {
    if (selectedStatus === "all") return null;

    if (selectedStatus === "approved") {
      return JSON.stringify({ isFreezed: false, isVerified: true });
    } else if (selectedStatus === "pending") {
      return JSON.stringify({
        $or: [
          { status: "pending" },
          { status: null },
          { isFreezed: false, isVerified: false },
        ],
      });
    } else if (selectedStatus === "frozen") {
      return JSON.stringify({ isFreezed: true });
    }

    return null;
  };

  // Combine search, status, and advanced filters
  const buildCombinedFilter = () => {
    const searchFilter = buildSearchFilter();
    const statusFilter = buildStatusFilter();
    const filters: any[] = [];

    if (searchFilter) {
      const parsed = JSON.parse(searchFilter);
      // Flatten nested $and structures
      if (parsed.$and && Array.isArray(parsed.$and)) {
        filters.push(...parsed.$and);
      } else {
        filters.push(parsed);
      }
    }
    if (statusFilter) {
      const parsed = JSON.parse(statusFilter);
      // Flatten nested $and structures
      if (parsed.$and && Array.isArray(parsed.$and)) {
        filters.push(...parsed.$and);
      } else {
        filters.push(parsed);
      }
    }
    if (advancedFilter) {
      const parsed = JSON.parse(advancedFilter);
      // Flatten nested $and structures
      if (parsed.$and && Array.isArray(parsed.$and)) {
        filters.push(...parsed.$and);
      } else {
        filters.push(parsed);
      }
    }

    if (filters.length === 0) return null;
    if (filters.length === 1) return JSON.stringify(filters[0]);

    // Combine all filters with AND
    return JSON.stringify({
      $and: filters,
    });
  };

  // Get organizations with filter based on selected status and search
  const { data, loading, error, refetch } = useQuery(GET_ORGANIZATIONS, {
    variables: {
      limit: advancedFilter ? 10000 : (debouncedSearchText.trim() ? 20 : pageSize), // Fetch all for client-side pagination when filtered
      page: advancedFilter ? 1 : (debouncedSearchText.trim() ? 1 : page), // Always page 1 for filtered/search results
      sort:
        sortModel.length > 0
          ? JSON.stringify({
              [sortModel[0].field]: sortModel[0].sort === "desc" ? -1 : 1,
            })
          : null,
      filter: buildCombinedFilter(),
    },
    fetchPolicy: "network-only",
  });

  // Get total counts for all statuses
  const { data: totalsData, refetch: refetchTotals } = useQuery(
    GET_ORGANIZATIONS_COUNT,
    {
      fetchPolicy: "network-only",
    }
  );

  useEffect(() => {
    if (totalsData?.totalOrganizations) {
      const { total, approved, pending, frozen } =
        totalsData.totalOrganizations;
      if (selectedStatus === "all") {
        setRowCount(total || 0);
      } else if (selectedStatus === "approved") {
        setRowCount(approved || 0);
      } else if (selectedStatus === "pending") {
        setRowCount(pending || 0);
      } else if (selectedStatus === "frozen") {
        setRowCount(frozen || 0);
      }
    }
  }, [selectedStatus, totalsData]);

  useEffect(() => {
    if (!loading && error) {
      console.error("An error occurred while fetching organizations:", error);
      toast.error("Failed to load organizations. Please try again.");
    }
  }, [data, error, loading]);

  const handleActionClick = useCallback(
    (action: any, details: Organization) => {
      console.log("Action clicked:", action, details);

      if (action === "status_updated") {
        // Trigger data refresh when status is updated
        refetch();
        refetchTotals();
      } else {
        // TODO: Implement other action handling (freeze/unfreeze, edit, etc.)
        toast.success("Action handling will be implemented later");
      }
    },
    [refetch, refetchTotals]
  );

  const displayedData = useMemo(() => {
    return data?.getOrganizations || [];
  }, [data]);

  // For server-side search, we don't need client-side filtering
  const filteredData: Organization[] | [] = useMemo(() => {
    return displayedData;
  }, [displayedData]);

  // Use client-side pagination when advanced filters are applied
  const paginationMode = advancedFilter ? "client" : "server";
  const effectiveRowCount = advancedFilter ? filteredData.length : rowCount;

  const handlePageChange = (newPage: number) => {
    setPage(newPage + 1);
  };

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setPage(1);
  };

  const handleSortModelChange = (newModel: any) => {
    const sortedModel = newModel.map((item: any) => ({
      field: item.field,
      sort: item.sort ?? ("asc" as GridSortDirection),
    }));
    setSortModel(sortedModel);
    setPage(1); // Reset to first page when sorting changes
  };

  const handleFilterModelChange = (model: any) => {
    const newQuery = model.quickFilterValues
      ? model.quickFilterValues.join(" ")
      : "";
    setSearchText(newQuery);
  };

  return (
    <PageContainer title="Organizations">
      <Box sx={{ p: 3 }}>
        {/* Add Organization Button and Filter */}
        <Box sx={{ mb: 3, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          {(admin?.role === "superadmin" || admin?.role === "admin") && (
            <Button
              onClick={() => setAddModalOpen(true)}
              variant="outlined"
              startIcon={<FcPlus />}
              sx={{
                color: "#1B8368",
                borderColor: "#1B8368",
                "&:hover": {
                  borderColor: "#1B8368",
                  backgroundColor: "rgba(27, 131, 104, 0.04)",
                },
              }}
            >
              Add New Organization
            </Button>
          )}
        </Box>

        {/* Statistics Cards */}
        <Grid container spacing={3} sx={{ mb: 3 }}>
          <Grid item xs={3} onClick={() => setSelectedStatus("all")}>
            <Card
              sx={{
                borderRadius: "22px",
                border: selectedStatus === "all" ? "2px solid #1B8368" : "none",
                boxShadow:
                  selectedStatus === "all"
                    ? "none"
                    : "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
                cursor: "pointer",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  color="#1B8368"
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  All Organizations
                </Typography>
                <Typography
                  variant="h5"
                  color="#1B8368"
                  sx={{ fontWeight: "bold" }}
                >
                  {(totalsData?.totalOrganizations?.total || 0)
                    .toString()
                    .padStart(2, "0")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={3} onClick={() => setSelectedStatus("approved")}>
            <Card
              sx={{
                borderRadius: "22px",
                border:
                  selectedStatus === "approved" ? "2px solid #1B8368" : "none",
                boxShadow:
                  selectedStatus === "approved"
                    ? "none"
                    : "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
                cursor: "pointer",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  color="#00D23B"
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  Approved Organizations
                </Typography>
                <Typography
                  variant="h5"
                  color="#00D23B"
                  sx={{ fontWeight: "bold" }}
                >
                  {(totalsData?.totalOrganizations?.approved || 0)
                    .toString()
                    .padStart(2, "0")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={3} onClick={() => setSelectedStatus("pending")}>
            <Card
              sx={{
                borderRadius: "22px",
                border:
                  selectedStatus === "pending" ? "2px solid #1B8368" : "none",
                boxShadow:
                  selectedStatus === "pending"
                    ? "none"
                    : "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
                cursor: "pointer",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  color="#EDA920"
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  Pending Organizations
                </Typography>
                <Typography
                  variant="h5"
                  color="#EDA920"
                  sx={{ fontWeight: "bold" }}
                >
                  {(totalsData?.totalOrganizations?.pending || 0)
                    .toString()
                    .padStart(2, "0")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={3} onClick={() => setSelectedStatus("frozen")}>
            <Card
              sx={{
                borderRadius: "22px",
                border:
                  selectedStatus === "frozen" ? "2px solid #1B8368" : "none",
                boxShadow:
                  selectedStatus === "frozen"
                    ? "none"
                    : "0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)",
                cursor: "pointer",
              }}
            >
              <CardContent>
                <Typography
                  variant="h6"
                  color="#FF4949"
                  sx={{ display: "flex", alignItems: "center", gap: "10px" }}
                >
                  Frozen Organizations
                </Typography>
                <Typography
                  variant="h5"
                  color="#FF4949"
                  sx={{ fontWeight: "bold" }}
                >
                  {(totalsData?.totalOrganizations?.frozen || 0)
                    .toString()
                    .padStart(2, "0")}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Table */}
        <OrganizationsTable
          loading={loading}
          filteredData={filteredData}
          handleActionClick={handleActionClick}
          page={page - 1}
          pageSize={pageSize}
          rowCount={effectiveRowCount}
          paginationMode={paginationMode}
          onPageChange={handlePageChange}
          onPageSizeChange={handlePageSizeChange}
          sortModel={sortModel}
          onSortModelChange={handleSortModelChange}
          onFilterModelChange={handleFilterModelChange}
          searchText={searchText}
          onOpenAdvancedFilter={() => setFilterBuilderOpen(true)}
          hasActiveFilter={!!advancedFilter}
        />

        {/* Add Organization Form Modal */}
        <OrganizationForm
          open={addModalOpen}
          onClose={() => setAddModalOpen(false)}
        />

        {/* Filter Builder */}
        <FilterBuilder
          entityType="ORGANIZATION"
          open={filterBuilderOpen}
          onClose={() => setFilterBuilderOpen(false)}
          onApply={(filter) => {
            setAdvancedFilter(filter);
            setPage(1);
          }}
          pageId="organizations-table"
        />
      </Box>
    </PageContainer>
  );
};

export default Organizations;
